import { useMutation, useQuery, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';

import { QUICK_LINK_QUERY_KEYS } from './keys';

import { updateLinkLibraryWeights, fetchQuickLinks } from 'services/quickLinks';
import { handleError } from 'utils/errorHandler';

import type { ApiEnvelope, ServicePayload } from 'interface/service';
import { LinkLibraryTreeNode } from 'interface/linkLibraryTree';

type QuickLinksItem = LinkLibraryTreeNode & { weight: number };
type QuickLinksData = QuickLinksItem[];
type QueryError = Error;

export const QUICK_LINKS_QUERY_KEY = [QUICK_LINK_QUERY_KEYS.QUICK_LINKS] as const;

const fetchQuickLinksData = async <TData>(): Promise<TData> => {
  return (await fetchQuickLinks()) as TData;
};

type UseFetchQuickLinksResult<TData> = UseQueryResult<TData, QueryError>;

type UseUpdateLinkLibraryMutationResult = {
  mutate: UseMutationResult<ApiEnvelope, QueryError, ServicePayload, void>['mutate'];
  isLoading: boolean;
};

export const useFetchQuickLinks = <TData = QuickLinksData>(): UseFetchQuickLinksResult<TData> => {
  return useQuery<TData, QueryError>({
    queryKey: QUICK_LINKS_QUERY_KEY,
    queryFn: () => fetchQuickLinksData<TData>(),
  });
};

export const useUpdateLinkLibraryMutation = (): UseUpdateLinkLibraryMutationResult => {
  const { mutate, isPending } = useMutation<ApiEnvelope, QueryError, ServicePayload>({
    mutationFn: updateLinkLibraryWeights,
    onError: (error) => handleError(error),
  });

  return {
    mutate,
    isLoading: isPending,
  };
};
